import { Box, Flex, FlexProps, useDisclosure } from "@chakra-ui/core"
import * as R from "ramda"
import React, { useState } from "react"
import loadable from "@loadable/component"
import { LaptopDownOnly, LaptopUpOnly, bpWithSidebar, bp } from "../../utils/MediaQueries"
import BoxSelector, { OptionType } from "../BoxSelector"
import ColorPicker, { ColorType, OnColorPickFunctionType } from "../ColorPicker"
import { Button, RainbowButton } from "../Buttons"
import Body from "../typography/Body"
import Heading from "../typography/Heading"
import BackInStockButton from "../BackInStockButton/connected"
import { decodeShopifyId } from "../../utils"
import { getBikeTypeCategory } from "../../utils/bikeUtils"
import FormattedTitle from "../FormattedTitle"
import { useSelector } from "react-redux"
import { BaseRootState } from "../../redux/store"
import SlashedText from "../SlashedText"
import useJudgeMeReviews from "../../hooks/useJudgeMeReviews"
import ReviewsButton from "../../components/ReviewsButton"
import ModalButton from "../../components/ModalButton"
import UpsellAccessories from "../../components/BikeProductPage/UpsellAccessories"
import { Accessory } from "views/BicycleProductPage"
import Notice from "../../components/Notice"
import "./style.css"

const LoadableAffirmWidget = loadable(() => import("../AffirmWidget"))

export const BikePageHeader: React.FC<{
  title: string
  isSideBarOpen?: boolean
}> = ({ title, isSideBarOpen }) => (
  <>
    <LaptopDownOnly isSideBarOpen={isSideBarOpen}>
      <Heading
        size="4"
        color="night"
        textTransform="none"
        letterSpacing="0.0313rem"
        mb="0.5938rem"
      >
        <FormattedTitle raw={title} />
      </Heading>
    </LaptopDownOnly>
    <LaptopUpOnly isSideBarOpen={isSideBarOpen}>
      <Heading
        size="3"
        color="night"
        textTransform="none"
        letterSpacing="0.0313rem"
        mb="0.5938rem"
      >
        <FormattedTitle raw={title} />
      </Heading>
    </LaptopUpOnly>
  </>
)

const LineDivider = () => (
  <Box h="1px" w="100%" bg="dividerLine" m="0.875rem 0" />
)

export const LabeledSelector: React.FC<
  {
    label: string
    selector: JSX.Element
  } & FlexProps
> = ({ label, selector, ...props }) => (
  <Flex justifyContent="space-between" alignItems="center" {...props}>
    <Body whiteSpace="nowrap" mr="1.375rem">
      {label}
    </Body>
    {selector}
  </Flex>
)

type OnSelectFunctionType = (name: string) => void

export interface ProductActionsProps {
  bikeTitle: string
  colorOptions: Array<ColorType>
  onColorSelect: OnColorPickFunctionType
  initialColorId?: string
  speedOptions: Array<OptionType>
  onSpeedSelect: OnSelectFunctionType
  initialSpeed?: string
  warrantyOptions: Array<OptionType>
  onWarrantySelect: OnSelectFunctionType
  onCustomize: VoidFunction
  onAddToCart: VoidFunction
  isCartLoading?: boolean
  price?: string
  compareAtPrice?: string
  variantId?: string
  saleStatus: "available" | "preorder" | "out-of-stock"
  hideWarranty?: boolean
  hideCustomizer?: boolean
  limitedQuantity?: number
  variant?: any
  bike?: any
  freeShipping?: any
  preorderLabel?: any
  isPreorder?: any
  currentSpeed?: any
  accessoryUpsell?: any
  onAccessoryChange?: Function
  selectedAccessories?: Accessory[]
}

const ProductActions: React.FC<ProductActionsProps & FlexProps> = ({
  bikeTitle,
  colorOptions,
  onColorSelect,
  initialColorId,
  speedOptions,
  onSpeedSelect,
  initialSpeed,
  onAddToCart,
  warrantyOptions,
  onWarrantySelect,
  onCustomize,
  isCartLoading,
  price,
  compareAtPrice,
  variantId,
  saleStatus,
  hideWarranty = false,
  hideCustomizer = false,
  limitedQuantity,
  variant,
  bike,
  freeShipping,
  preorderLabel,
  isPreorder,
  currentSpeed,
  accessoryUpsell,
  onAccessoryChange,
  selectedAccessories,
  ...props
}) => {
  const { isSideBarOpen } = useSelector((state: BaseRootState) => ({
    isSideBarOpen: state.sidebar.isOpen,
  }))

  const sortedSpeedOptions = React.useMemo(
    () =>
      R.sort(
        R.comparator((a, b) => Number(a.name) < Number(b.name)),
        speedOptions || []
      ),
    [speedOptions]
  )

  const initialSpeedIndex = sortedSpeedOptions?.reduce((acc, cur, index) => {
    if (cur.name === initialSpeed) return index
    return acc
  }, undefined)

  const [currentWarrantyPrice, setCurrentWarrantyPrice] = useState(0)
  const activeWarrantyOption =
    warrantyOptions?.[currentWarrantyPrice] ?? warrantyOptions?.[0]
  const extendedWarrantyModal = useDisclosure()

  React.useEffect(() => {
    if (
      warrantyOptions &&
      currentWarrantyPrice >= warrantyOptions.length
    ) {
      setCurrentWarrantyPrice(0)
    }
  }, [warrantyOptions, currentWarrantyPrice])

  const {
    enableFreeShippingMessage,
    enable365DayTestRideMessage,
    enable90DayTestRideMessage,
    enableNationwideServiceMessage,
    enableFreeHelmetMessage,
    enableWarrantyMessage,
    disableWarranty,
  } = bike
  // Reviews
  const reviews = useJudgeMeReviews(variant?.product.internalTitle)

  // Determine bike type category for badge visibility
  const bikeTypeCategory = getBikeTypeCategory(bike)
  const isStandardBike = bikeTypeCategory === "standard"
  const isOpenBoxReturn = bike?.formattedTitle
    ?.toUpperCase()
    .includes("OPEN BOX RETURN")

 function getExtendedWarrantyContent(): string {
  return `
        <div class="extended-warranty">
          <h3>Extended Warranty Coverage – Year 2 Protection</h3>

          <p>
            The <strong>sixthreezero Extended Warranty</strong> adds
            <strong>one additional year of coverage</strong> to your electric bike or
            electric trike, extending total coverage to
            <strong>24 months from the original delivery date</strong>.
          </p>

          <h4>What’s Covered</h4>
          <p>
            During the second year of ownership, the extended warranty covers
            <strong>both parts and labor</strong> for the following components in the
            event of a manufacturer-related failure:
          </p>

          <h5>Electrical Components</h5>
          <ul>
            <li>Battery</li>
            <li>Motor</li>
            <li>Controller</li>
            <li>Display</li>
            <li>Wiring and electrical connections</li>
          </ul>

          <h5>Non-Electrical Components</h5>
          <ul>
            <li>Frame and structural components</li>
            <li>Drivetrain components</li>
            <li>Brakes (excluding wear items)</li>
          </ul>

          <h4>Exclusions</h4>
          <ul>
            <li>Tires and inner tubes</li>
            <li>Rims and wheels</li>
            <li>Normal wear-and-tear items</li>
            <li>
              Damage caused by misuse, accidents, water intrusion, or unauthorized
              modifications
            </li>
          </ul>

          <h4>Battery Warranty Process</h4>
          <p>If a battery warranty claim is submitted:</p>
          <ul>
            <li>The battery must be returned to sixthreezero for diagnostic testing</li>
            <li>Return shipping is the responsibility of the warranty holder</li>
            <li>Upon approval, a replacement battery will be shipped at no cost</li>
          </ul>

          <h4>Labor Coverage</h4>
          <ul>
            <li>
              Labor may be performed by a sixthreezero approved service provider or
              coordinated directly through sixthreezero
            </li>
          </ul>

          <h4>Important Notes</h4>
          <ul>
            <li>The extended warranty is optional and must be purchased separately</li>
            <li>
              Coverage begins immediately after the standard 12-month warranty expires
            </li>
            <li>The warranty is non-transferable</li>
          </ul>
        </div>
    `.trim();
}


  return (
    <Flex flexDirection="column" {...props}>
      <LaptopUpOnly isSideBarOpen={isSideBarOpen}>
        <BikePageHeader title={bikeTitle} isSideBarOpen={isSideBarOpen} />
        <Flex>
          <Body fontWeight="bold" color="night" mb="0.2rem">
            {price}
          </Body>
          {compareAtPrice && (
            <SlashedText ml="1rem">{compareAtPrice}</SlashedText>
          )}
        </Flex>
        <LoadableAffirmWidget
          pageType="product"
          amount={Number(price?.replace("$", "")) || 0}
        />
        <ReviewsButton
          bikeName={`${bike.title} ${currentSpeed} Speed`}
          mt="1.2rem"
          w="fit-content"
          mb="1.2875rem"
          isLoading={reviews.isLoading}
          rating={reviews.avgRating}
          reviewCount={reviews.numReviews}
          reviews={R.uniqBy(R.prop("id"), reviews.seoReviews)}
          productId={reviews.judgemeProduct?.product?.id}
          loadMoreReviews={reviews.hasMoreToLoad ? reviews.loadMore : undefined}
          isLoadingMore={reviews.isLoadingMore}
        />
      </LaptopUpOnly>
      <Box mt={["0rem", null, null, null, "0.5625rem"]}>
        {(enableFreeShippingMessage ||
          enable365DayTestRideMessage ||
          enable90DayTestRideMessage || enableNationwideServiceMessage || enableWarrantyMessage || enableFreeHelmetMessage) && (
          <Flex
            w="100%"
            mb="1.2875rem"
            flexWrap={bp("wrap", "wrap")}
            justifyContent={bp("center", "left")}
            alignItems="baseline"
            gridRowGap="5px"
          >
            {enableNationwideServiceMessage ? (
                <ModalButton
                  iconName="nationwideService"
                  text="Nationwide Service"
                  title="Nationwide Service"
                  modalBody={"We have relationships with mechanics across the country to ensure you can get the proper service on your sixthreezero bike, ebike or etrike. Should you ever need a mechanic to perform a repair, maintenance or warranty service we will find an approved mechanic that can work on your sixthreezero. We've built this network to give you peace of mind knowing if you buy from sixthreezero online you'll get the proper service during ownership."}
                  w={isSideBarOpen ? "7rem" : "8rem"}
                  fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                  mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
                />
              ) : null}

            {!isStandardBike && (enable365DayTestRideMessage ? (
              <ModalButton
                iconName="testRide"
                text="30 Day Test Ride"
                title="30 Day Test Ride"
                modalBody={"What's this mean? Exactly what it says. We don't care what you do, ride It, store It, take it to buy groceries, get some exercise, the choice is yours. Either way if anytime between when you receive the bike and 30 Days you decide you don't love the experience, send it back, and we'll pay the shipping. We understand it may get some scratches, dents, and dings. We get it, you tested it. We don't charge restocking fees either. We're committed to you loving your bike, and don't want you keeping it unless you do."}
                w={isSideBarOpen ? "7rem" : "8rem"}
                fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
              />
            ) : enable90DayTestRideMessage ? (
              <ModalButton
                iconName="testRide"
                text="30 Day Test Ride"
                title="30 Day Test Ride"
                modalBody={"What's this mean? Exactly what it says. We don't care what you do, ride It, store It, take it to buy groceries, get some exercise, the choice is yours. Either way if anytime between when you receive the bike and 30 Days you decide you don't love the experience, send it back, and we'll pay the shipping. We understand it may get some scratches, dents, and dings. We get it, you tested it. We don't charge restocking fees either. We're committed to you loving your bike, and don't want you keeping it unless you do."}
                w={isSideBarOpen ? "7rem" : "8rem"}
                fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
              />
            ) : null)}
            <ModalButton
              iconName="shipping"
              text="Free Shipping"
              title="Free Shipping"
              modalBody={"Free Nationwide Shipping within the Continental United States. Alaska, and Hawaii are excluded from free shipping, however the rate will be discounted by the maximum amount of shipping cost in the continental USA. Alaska and Hawaii customers please, note that sixthreezero Electric Bicycles are larger, and heavier shipments and that will more to ship. We will ship to home and office directly via FedEx. All Bicycles arrive 80% assembled in box. Orders will generally ship within five business days and in most cases you will receive your order in about one week. For continental USA orders, we will pay to ship your bike to you, and in the case that you wish to return, we’ll pay to have it shipped back to sixthreezero."}
              w={isSideBarOpen ? "7rem" : "8rem"}
              fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
              mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
            />
            { enableWarrantyMessage ? (
              <>
                <ModalButton
                  iconName="warranty"
                  text="Warranty"
                  title="Warranty"
                  modalBody={"A One Year Standard Warranty is included with all sixthreezero bicycles when purchased new from sixthreezero.com. This warranty is limited to 1 Year from the date of delivery, so please register your bike the day you receive it. Proof of purchase will be required for any warranty claim within this 1 year period. Warranty is non-transferrable."}
                  w={isSideBarOpen ? "7rem" : "8rem"}
                  fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                  mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
                />
              {bike.type?.includes("Electric") && !isOpenBoxReturn && (
                <ModalButton
                iconName="warranty"
                text="Extended Warranty"
                title="Extended Warranty"
                modalBody={getExtendedWarrantyContent()}
                w={isSideBarOpen ? "7rem" : "8rem"}
                fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
                isHtml={true}
              />
              )}
            </>
            ) : null }

            {enableFreeHelmetMessage ? (
	            <ModalButton
                iconName="helmate"
                text="Free Helmet for Seniors"
                title="Free Helmet for Seniors"
                modalBody={"When you order an e-trike from SixThreeZero and are 65 or older, you will receive a free helmet with your purchase. Simply place your order as usual, then email a photo of your ID to theteam@sixthreezero.com to claim your helmet. If you have any questions, feel free to call customer service at (310)982-2877."}
                w={isSideBarOpen ? "7rem" : "8rem"}
                h={"5rem"}
                fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
              />
            ) : null }

                <ModalButton
                  iconName="fulfillmentFee"
                  text="Fulfillment & Inspection Fee"
                  title="Fulfillment & Inspection Fee"
                  modalBody={"A small handling fee is added to each order to support the extra care that goes into your purchase. Every item is carefully inspected for defects, accuracy, and proper operation before it leaves our facility, and then packed using high‑quality materials to help prevent damage during shipping. This fee also helps cover the trained team and processes that ensure you consistently receive your products in excellent condition. Non-refundable."}
                  w={isSideBarOpen ? "7rem" : "8rem"}
                  fontSize={isSideBarOpen ? "0.8rem" : "0.9rem"}
                  mb={bpWithSidebar("0.75rem", "0", isSideBarOpen)}
                />
                
            {/* {enable365DayTestRideMessage ? (
              <ModalButton
                iconName="heartCheck"
                text="30 Day Test Ride"
                title="30 Day Test Ride"
                modalBody="What's this mean? Exactly what it says. We don't care what you do, ride It, store It, take it to buy groceries, get some exercise, the choice is yours. Either way if anytime between when you receive the bike and 30 Days you decide you don't love the experience, send it back, and we'll pay the shipping. We understand it may get some scratches, dents, and dings. We get it, you tested it. We don't charge restocking fees either. We're committed to you loving your bike, and don't want you keeping it unless you do. The 30 day return policy only applies to our standard analog bicycles; this return policy does not apply to our electric-powered bicycles. Please see below for the details regarding our electric bike return policy."
              />
            ) : enable90DayTestRideMessage ? (
              <ModalButton
                iconName="heartCheck"
                text="30 Day Test Ride"
                title="30 DAY TEST RIDE"
                modalBody="What's this mean? Exactly what it says. We don't care what you do, ride It, store It, take it to buy groceries, get some exercise, the choice is yours. Either way if anytime between when you receive the bike and 30 Days you decide you don't love the experience, send it back, and we'll pay the shipping. We understand it may get some scratches, dents, and dings. We get it, you tested it. We don't charge restocking fees either. We're committed to you loving your bike, and don't want you keeping it unless you do."
              />
            ) : null} */}
          </Flex>
        )}
      </Box>
      <Box mt={[null, null, null, null, "2.125rem"]}>
        <LabeledSelector
          label="Color"
          selector={
            <ColorPicker
              colors={colorOptions}
              onColorClick={onColorSelect}
              borderType="solid"
              selectedColorId={initialColorId}
            />
          }
        />
        <LineDivider />
        <LabeledSelector
          label="Speed"
          selector={
            <BoxSelector
              options={sortedSpeedOptions}
              onSelect={onSpeedSelect}
              title="speed"
              defaultSelectedIndex={initialSpeedIndex}
            />
          }
        />
        {!hideWarranty && (
          <>
            <LineDivider />
            <LabeledSelector
              label={
                activeWarrantyOption?.subText
                  ? `Warranty ${activeWarrantyOption.subText}`
                  : "Warranty"
              }
              selector={
                <BoxSelector
                  options={
                    isOpenBoxReturn
                      ? warrantyOptions?.filter(
                          (o) => o.name !== "Extended Warranty"
                        ) ?? []
                      : warrantyOptions ?? []
                  }
                  onSelect={(name) => {
                    onWarrantySelect(name)
                    const selectedIndex = warrantyOptions.findIndex(
                      (option) => option.name === name
                    )
                    setCurrentWarrantyPrice(
                      selectedIndex >= 0 ? selectedIndex : 0
                    )
                    // Open Extended Warranty modal when "Extended Warranty" option is selected
                    if (name === "Extended Warranty" && (bike?.type?.includes("Electric") || bike?.style?.includes("Tricycle"))) {
                      extendedWarrantyModal.onOpen()
                    }
                  }}
                  title="warranty"
                  mobileView="warranty"
                />
              }
            />
          </>
        )}
      </Box>

      {accessoryUpsell && (
        <UpsellAccessories
          upsellProducts={accessoryUpsell}
          onAccessoryChange={onAccessoryChange}
          selectedAccessories={selectedAccessories}
        />
      )}

      <Flex w="100%" mt={["2.8125rem", null, null, null, "3.875rem"]}>
        {saleStatus === "available" && (
          <>
            {!hideCustomizer && (
              <RainbowButton
                w="8.6875rem"
                mr="1.25rem"
                px={0}
                onClick={onCustomize}
              >
                Customize
              </RainbowButton>
            )}

            <Button
              flexGrow={1}
              onClick={onAddToCart}
              isLoading={isCartLoading}
              px={0}
            >
              Add to Cart
            </Button>
          </>
        )}
        {saleStatus === "preorder" && (
          <>
            {!hideCustomizer && (
              <RainbowButton
                w="8.6875rem"
                mr="1.25rem"
                px={0}
                onClick={onCustomize}
              >
                Customize
              </RainbowButton>
            )}
            <Button
              flexGrow={1}
              onClick={onAddToCart}
              isLoading={isCartLoading}
            >
              Pre-order
            </Button>
          </>
        )}
        {saleStatus === "out-of-stock" && (
          <Box w="100%">
            <Flex mb="1.25rem">
              {!hideCustomizer && (
                <RainbowButton
                  w="8.6875rem"
                  mr="1.25rem"
                  px={0}
                  onClick={onCustomize}
                >
                  Customize
                </RainbowButton>
              )}
              <Button flexGrow={1} theme="secondary" isDisabled>
                Sold Out
              </Button>
            </Flex>
          </Box>
        )}
      </Flex>
      {limitedQuantity && (
        <Body textAlign="center" color="dawn" mt="1.25rem">
          Limited Quantity Remaining: Only {limitedQuantity} left
        </Body>
      )}
      {isPreorder && <Notice mt="1rem">{preorderLabel}</Notice>}
      {saleStatus === "preorder" && variant?.hideNotifyMeButton !== false && (
          <Box mt="1rem">
            <BackInStockButton
            variantId={decodeShopifyId(variantId) || ""}
            bikeTitle={variant.internalTitle}
            listName="Pre-order"
            w="100%"
            />
          </Box>
        )}
      {saleStatus === "out-of-stock"  && variant?.hideNotifyMeButton !== false && (
        <Box mt="1rem">
        <BackInStockButton
        variantId={decodeShopifyId(variantId) || ""}
        bikeTitle={variant.internalTitle}
        listName="BackInStock"
        w="100%"
        />
      </Box>
      )}
      {/* Extended Warranty Modal - controlled externally when warranty option is selected */}
      {(bike?.type?.includes("Electric") || bike?.style?.includes("Tricycle")) &&
        !isOpenBoxReturn && (
        <ModalButton
          iconName="warranty"
          text="Extended Warranty"
          title="Extended Warranty"
          modalBody={getExtendedWarrantyContent()}
          isHtml={true}
          isOpen={extendedWarrantyModal.isOpen}
          onOpen={extendedWarrantyModal.onOpen}
          onClose={extendedWarrantyModal.onClose}
          hideButton={true}
        />
      )}
    </Flex>
  )
}

export default ProductActions
